import { DownOutlined, EditOutlined, ReloadOutlined, UpOutlined } from '@ant-design/icons';
import { RegPlate, SharedCard, VehicleEditDrawer } from '@components';
import { useDemoGuard, useDemoLookupAllowance } from '@hooks';
import { L } from '@lib';
import { RefreshVehicleData, Vehicle } from '@services';
import { useStore } from '@stores';
import { Button, Col, Form, Input, Row, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { VehicleDescriptions } from './VehicleDescriptions';

interface IProps {
  vehicle: Vehicle;
  showRefresh?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  bordered?: boolean;
  hideHeader?: boolean;
  setSelectedVehicle?: (vehicle: Vehicle | null) => void;
  /** Label/value pairs per row; a narrow host (a drawer) asks for fewer and grows downwards. */
  descriptionColumns?: React.ComponentProps<typeof VehicleDescriptions>["column"];
  descriptionLabelWidth?: number;
}

export const VehicleDetailsCard: React.FC<IProps> = observer(
  ({ vehicle, showRefresh, collapsible = false, defaultExpanded = true, bordered = true, hideHeader = false, setSelectedVehicle, descriptionColumns, descriptionLabelWidth }) => {
    const { vehicleStore } = useStore();
    const [refreshForm] = Form.useForm();
    const [showEdit, setShowEdit] = useState(false);
    const [expanded, setExpanded] = useState(defaultExpanded);
    // Unique per vehicle so multiple cards on one page don't share a DOM id.
    const refreshFormId = `vehicle-vin-form-${vehicle.id}`;

    const { exhausted, consume: consumeLookup } = useDemoLookupAllowance();
    const { showDemoNotice } = useDemoGuard();

    // A refresh always reaches UKVD — there is no cached path for it — so unlike the
    // registration search it spends a credit on every successful call.
    const onRefreshVehicleData = useCallback(
      async (values: RefreshVehicleData) => {
        if (exhausted) {
          showDemoNotice();
          return;
        }

        await vehicleStore.refreshVehicleData(values).then(async (result) => {
          if (!result) {
            setShowEdit(true);
          } else {
            await consumeLookup();
            setSelectedVehicle?.(result);
            refreshForm.resetFields();
          }
        });
      },
      [vehicleStore, refreshForm, setSelectedVehicle, exhausted, consumeLookup, showDemoNotice]
    );

    const cardContent = (
      <>
        {!hideHeader && (
          <Row
            justify="space-between"
            align="middle"
            style={{ marginTop: 8, marginBottom: collapsible && !expanded ? 8 : 12, cursor: collapsible ? 'pointer' : undefined }}
            onClick={collapsible ? () => setExpanded((prev) => !prev) : undefined}
          >
            <Col>
              <RegPlate>{vehicle.registrationNumber}</RegPlate>
            </Col>
            {collapsible && (
              <Col>
                {expanded ? <UpOutlined /> : <DownOutlined />}
              </Col>
            )}
          </Row>
        )}
        {(!collapsible || expanded) && (
          <>
            {(showRefresh || vehicle.manuallyAdded) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 12,
                  flexWrap: 'wrap',
                }}
              >
                {showRefresh && (
                  <Form
                    layout="inline"
                    id={refreshFormId}
                    form={refreshForm}
                    onFinish={onRefreshVehicleData}
                    initialValues={{ registrationNumber: vehicle.registrationNumber }}
                  >
                    <Form.Item>
                      <Typography.Text strong>Not your vehicle?</Typography.Text>
                    </Form.Item>
                    <Form.Item hidden name="registrationNumber">
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="vin"
                      required
                      rules={[{ required: true, message: 'Please enter a VIN' }]}
                    >
                      <Input placeholder="Enter vin" />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        form={refreshFormId}
                        icon={<ReloadOutlined />}
                        loading={vehicleStore.refreshLoading}
                        disabled={vehicleStore.refreshLoading}
                      >
                        Refresh
                      </Button>
                    </Form.Item>
                  </Form>
                )}
                {vehicle.manuallyAdded && (
                  <Button type="primary" icon={<EditOutlined />} onClick={() => setShowEdit(true)}>
                    {L('Update')}
                  </Button>
                )}
              </div>
            )}
            <VehicleDescriptions
              vehicle={vehicle}
              column={descriptionColumns}
              labelWidth={descriptionLabelWidth}
            />
          </>
        )}
      </>
    );

    return (
      <>
        {bordered ? <SharedCard size="small">{cardContent}</SharedCard> : cardContent}
        <VehicleEditDrawer
          open={showEdit}
          vehicle={vehicle}
          onClose={() => setShowEdit(false)}
          onSuccess={(result) => setSelectedVehicle?.(result)}
        />
      </>
    );
  }
);
